import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function LessonContent({ data }) {
  const navigate = useNavigate();
  const modules = data?.modules || [];
  const title = data?.title || "Lesson Content";
  const role = sessionStorage.getItem("role");

  // Function to handle material download
  const handleMaterialDownload = (material) => {
    if (!material.filepath) {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Material file path not found',
        confirmButtonColor: '#007bff'
      });
      return;
    }

    // Show downloading toast
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });

    Toast.fire({
      icon: 'info',
      title: `Downloading ${material.filename || 'file'}...`
    });

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = material.filepath;
    link.download = material.filename || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success message
    setTimeout(() => {
      Toast.fire({
        icon: 'success',
        title: 'Download started!'
      });
    }, 500);
  };

  // Function to check if current time is within the allowed date range
  const isWithinDateRange = (sequence) => {
    if (sequence.is_date_specific !== 1) {
      return true;
    }

    const now = new Date();
    const startDate = new Date(sequence.start_datetime);
    const endDate = new Date(sequence.end_datetime);

    return now >= startDate && now <= endDate;
  };

  // Function to format date for display
  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Function to show date-specific locked assessment modal
  const showDateSpecificLockedModal = (sequence) => {
    const now = new Date();
    const startDate = sequence.start_datetime ? new Date(sequence.start_datetime) : null;
    const endDate = sequence.end_datetime ? new Date(sequence.end_datetime) : null;

    let message = '';
    let title = '';

    if (!sequence.start_datetime || !sequence.end_datetime) {
      title = 'Assessment Coming Soon';
      message = `
        <div style="text-align: center; padding: 10px;">
          <div style="margin-bottom: 20px;">
            <i class="ri-calendar-todo-line" style="font-size: 55px; color: #6f42c1;"></i>
          </div>
          <h4 style="color: #495057; margin-bottom: 5px;">${sequence.title}</h4>
          <p style="margin-bottom: 20px; color: #6c757d; font-size: 16px;">This final assessment will be available soon.</p>
          <div style="background: #f3e5f5; padding: 10px; border-radius: 12px; margin: 10px 0;">
            <div style="margin-bottom: 15px;">
              <i class="ri-time-line" style="color: #6f42c1; font-size: 24px;"></i>
            </div>
            <p style="margin: 0; color: #6f42c1; font-size: 16px; font-weight: 600;">
              📅 Dates and times are being finalized
            </p>
            <p style="margin: 8px 0 0 0; color: #8e44ad; font-size: 14px;">
              You will be notified when the assessment becomes available
            </p>
          </div>
          <div style="background: #e8f5e8; padding: 5px; border-radius: 8px; margin-top: 10px; border-left: 4px solid #28a745;">
            <p style="margin: 0; color: #155724; font-size: 14px;">
              <i class="ri-lightbulb-line me-2" style="color: #28a745;"></i>
              <strong>Tip:</strong> Continue with other course materials while you wait. The assessment will be announced when ready.
            </p>
          </div>
        </div>
      `;
    } else if (now < startDate) {
      title = 'Assessment Not Yet Available';
      message = `
        <div style="text-align: center; padding: 10px;">
          <div style="margin-bottom: 20px;">
            <i class="ri-time-line" style="font-size: 48px; color: #17a2b8;"></i>
          </div>
          <h4 style="color: #495057; margin-bottom: 5px;">${sequence.title}</h4>
          <p style="margin-bottom: 10px; color: #6c757d;">This final assessment will be available on:</p>
          <div style="background: #e3f2fd; padding: 5px; border-radius: 8px; margin: 10px 0;">
            <strong style="color: #1976d2; font-size: 16px;">
              <i class="ri-calendar-line me-2"></i>
              ${formatDateTime(sequence.start_datetime)}
            </strong>
          </div>
          <p style="margin-bottom: 10px; color: #6c757d;">And will remain available until:</p>
          <div style="background: #fff3cd; padding: 5px; border-radius: 8px; margin: 10px 0;">
            <strong style="color: #856404; font-size: 16px;">
              <i class="ri-calendar-close-line me-2"></i>
              ${formatDateTime(sequence.end_datetime)}
            </strong>
          </div>
          <div style="background: #f8f9fa; padding: 5px; border-radius: 8px; margin-top: 10px; border-left: 4px solid #28a745;">
            <p style="margin: 0; color: #495057; font-size: 14px;">
              <i class="ri-lightbulb-line me-2" style="color: #28a745;"></i>
              <strong>Tip:</strong> Make sure to complete the assessment within the available time window.
            </p>
          </div>
        </div>
      `;
    } else if (now > endDate) {
      title = 'Assessment No Longer Available';
      message = `
        <div style="text-align: center; padding: 10px;">
          <div style="margin-bottom: 20px;">
            <i class="ri-calendar-close-line" style="font-size: 48px; color: #dc3545;"></i>
          </div>
          <h4 style="color: #495057; margin-bottom: 5px;">${sequence.title}</h4>
          <p style="margin-bottom: 10px; color: #6c757d;">This final assessment was available from:</p>
          <div style="background: #f8f9fa; padding: 5px; border-radius: 8px; margin: 15px 0;">
            <strong style="color: #495057; font-size: 16px;">
              <i class="ri-calendar-line me-2"></i>
              ${formatDateTime(sequence.start_datetime)}
            </strong>
          </div>
          <p style="margin-bottom: 10px; color: #6c757d;">Until:</p>
          <div style="background: #f5c6cb; padding: 5px; border-radius: 8px; margin: 15px 0;">
            <strong style="color: #721c24; font-size: 16px;">
              <i class="ri-calendar-close-line me-2"></i>
              ${formatDateTime(sequence.end_datetime)}
            </strong>
          </div>
          <div style="background: #f8d7da; padding: 5px; border-radius: 8px; margin-top: 10px; border-left: 4px solid #dc3545;">
            <p style="margin: 0; color: #721c24; font-size: 14px;">
              <i class="ri-alert-line me-2" style="color: #dc3545;"></i>
              <strong>Notice:</strong> The assessment period has ended and is no longer accessible.
            </p>
          </div>
        </div>
      `;
    }

    Swal.fire({
      title: title,
      html: message,
      confirmButtonColor: '#007bff',
      confirmButtonText: 'Understood',
      width: '600px',
      customClass: {
        popup: 'rounded-lg'
      }
    });
  };

  // Function to check if all previous content is completed
  const isAllPreviousContentCompleted = (currentModuleIndex) => {
    for (let i = 0; i < currentModuleIndex; i++) {
      const module = modules[i];
      const sequences = Array.isArray(module.sequences)
        ? module.sequences
        : Object.values(module.sequences);

      for (const sequence of sequences) {
        if (sequence.type === 'Chapter') {
          if (sequence.ismarkedcompleted !== 1) {
            return false;
          }
        } else if (sequence.type === 'Quiz') {
          if (sequence.is_quiz_submitted !== 1) {
            return false;
          }
        }
      }
    }

    const currentModule = modules[currentModuleIndex];
    const currentSequences = Array.isArray(currentModule.sequences)
      ? currentModule.sequences
      : Object.values(currentModule.sequences);

    for (const sequence of currentSequences) {
      if (sequence.sub_type === 'exam') {
        continue;
      }

      if (sequence.type === 'Chapter') {
        if (sequence.ismarkedcompleted !== 1) {
          return false;
        }
      } else if (sequence.type === 'Quiz' && sequence.sub_type !== 'exam') {
        if (sequence.is_quiz_submitted !== 1) {
          return false;
        }
      }
    }

    return true;
  };

  // Function to check if a sequence is locked
  const isSequenceLocked = (sequence, moduleIndex) => {
    const programCategory = sessionStorage.getItem("program_category");

    if (programCategory === "middle-school-cbse") {
      return { locked: false, reason: null };
    }
    if (role !== "Student") return { locked: false, reason: null };

    if (sequence.type === 'Quiz' && sequence.sub_type === 'exam') {
      if (!isAllPreviousContentCompleted(moduleIndex)) {
        return { locked: true, reason: 'prerequisites' };
      }

      if (sequence.is_date_specific === 1 && !isWithinDateRange(sequence)) {
        return { locked: true, reason: 'date_specific' };
      }
    }

    return { locked: false, reason: null };
  };

  // Function to get incomplete content details as objects
  const getIncompleteContentDetails = (currentModuleIndex) => {
    const incompleteItems = [];

    for (let i = 0; i < currentModuleIndex; i++) {
      const module = modules[i];
      const sequences = Array.isArray(module.sequences)
        ? module.sequences
        : Object.values(module.sequences);

      sequences.forEach((sequence, seqIdx) => {
        if (sequence.type === 'Chapter' && sequence.ismarkedcompleted !== 1) {
          incompleteItems.push({
            moduleTitle: module.module_title,
            pendingType: 'Pending Chapter',
            sequenceTitle: sequence.title,
            moduleIndex: i,
            sequenceIndex: seqIdx,
            sequence: sequence
          });
        } else if (sequence.type === 'Quiz' && sequence.is_quiz_submitted !== 1) {
          incompleteItems.push({
            moduleTitle: module.module_title,
            pendingType: 'Pending Quiz',
            sequenceTitle: sequence.title,
            moduleIndex: i,
            sequenceIndex: seqIdx,
            sequence: sequence
          });
        }
      });
    }

    const currentModule = modules[currentModuleIndex];
    const currentSequences = Array.isArray(currentModule.sequences)
      ? currentModule.sequences
      : Object.values(currentModule.sequences);

    currentSequences.forEach((sequence, seqIdx) => {
      if (sequence.sub_type === 'exam') return;

      if (sequence.type === 'Chapter' && sequence.ismarkedcompleted !== 1) {
        incompleteItems.push({
          moduleTitle: currentModule.module_title,
          pendingType: 'Pending Chapter',
          sequenceTitle: sequence.title,
          moduleIndex: currentModuleIndex,
          sequenceIndex: seqIdx,
          sequence: sequence
        });
      } else if (sequence.type === 'Quiz' && sequence.sub_type !== 'exam' && sequence.is_quiz_submitted !== 1) {
        incompleteItems.push({
          moduleTitle: currentModule.module_title,
          pendingType: 'Pending Quiz',
          sequenceTitle: sequence.title,
          moduleIndex: currentModuleIndex,
          sequenceIndex: seqIdx,
          sequence: sequence
        });
      }
    });

    return incompleteItems;
  };

  // Function to handle navigation to incomplete content
  const navigateToIncompleteContent = (item) => {
    handleSequenceClick(item.sequence, item.moduleIndex, item.sequenceIndex);
  };

  // Function to show interactive incomplete content modal
  const showIncompleteContentModal = (currentModuleIndex) => {
    const incompleteItems = getIncompleteContentDetails(currentModuleIndex);

    const createIncompleteItemsList = (items, showAll = false) => {
      const itemsToShow = showAll ? items : items.slice(0, 5);
      const hasMore = items.length > 5 && !showAll;
      const hasQuizzes = items.some(item => item.pendingType === 'Pending Quiz');
      const contentText = hasQuizzes ? "Chapters and Quizzes" : "Chapters";

      return `
        <div style="text-align: left; max-height: ${showAll ? '300px' : 'auto'}; overflow-y: ${showAll ? 'auto' : 'visible'};">
          <p style="margin-bottom: 0px; text-align: center;">Complete all previous ${contentText} before accessing this assessment:</p>
          <div style="margin: 10px 0; ">
            ${itemsToShow.map((item, index) => `
              <div 
                class="incomplete-item" 
                style="
                  padding: 8px 12px; 
                  margin: 5px 0; 
                  background: #f8f9fa; 
                  border-left: 3px solid #007bff; 
                  cursor: pointer; 
                  border-radius: 4px;
                  transition: background-color 0.2s;
                "
                data-item-index="${items.indexOf(item)}"
                onmouseover="this.style.backgroundColor='#e9ecef'"
                onmouseout="this.style.backgroundColor='#f8f9fa'"
              >
                <div style="display: flex; align-items: center; gap: 8px;">
                  <i class="${item.sequence.type === 'Chapter' ? 'ri-play-circle-line' : 'ri-question-line'}" 
                     style="color: #007bff; font-size: 16px;"></i>
                  <div>
                    <div style="font-weight: 500; color: #495057; font-size: 14px;">
                      ${item.moduleTitle}<br/>
                      <strong>${item.pendingType}</strong> - ${item.sequenceTitle}
                    </div>
                  </div>
                </div>
              </div>
            `).join('')}
            ${hasMore ? `
              <div 
                id="view-more-btn" 
                style="
                  text-align: center; 
                  padding: 8px; 
                  background: #e3f2fd; 
                  color: #1976d2; 
                  cursor: pointer; 
                  border-radius: 4px; 
                  font-weight: 500;
                "
                onmouseover="this.style.backgroundColor='#bbdefb'"
                onmouseout="this.style.backgroundColor='#e3f2fd'"
              >
                View ${items.length - 5} more items...
              </div>
            ` : ''}
            ${showAll && items.length > 5 ? `
              <div 
                id="view-less-btn" 
                style="
                  text-align: center; 
                  padding: 8px; 
                  background: #f5f5f5; 
                  color: #666; 
                  cursor: pointer; 
                  border-radius: 4px; 
                  font-weight: 500;
                "
                onmouseover="this.style.backgroundColor='#e0e0e0'"
                onmouseout="this.style.backgroundColor='#f5f5f5'"
              >
                View Less
              </div>
            ` : ''}
          </div>
        </div>
      `;
    };

    Swal.fire({
      icon: 'warning',
      title: "Assessment Locked",
      html: createIncompleteItemsList(incompleteItems),
      confirmButtonColor: '#eee127ff',
      confirmButtonText: 'OK',
      width: '500px',
      didOpen: () => {
        document.querySelectorAll('.incomplete-item').forEach(item => {
          item.addEventListener('click', () => {
            const itemIndex = parseInt(item.getAttribute('data-item-index'));
            const selectedItem = incompleteItems[itemIndex];
            Swal.close();
            navigateToIncompleteContent(selectedItem);
          });
        });

        const viewMoreBtn = document.getElementById('view-more-btn');
        if (viewMoreBtn) {
          viewMoreBtn.addEventListener('click', () => {
            Swal.update({
              html: createIncompleteItemsList(incompleteItems, true)
            });

            setTimeout(() => {
              document.querySelectorAll('.incomplete-item').forEach(item => {
                item.addEventListener('click', () => {
                  const itemIndex = parseInt(item.getAttribute('data-item-index'));
                  const selectedItem = incompleteItems[itemIndex];
                  Swal.close();
                  navigateToIncompleteContent(selectedItem);
                });
              });

              const viewLessBtn = document.getElementById('view-less-btn');
              if (viewLessBtn) {
                viewLessBtn.addEventListener('click', () => {
                  Swal.update({
                    html: createIncompleteItemsList(incompleteItems, false)
                  });

                  setTimeout(() => {
                    document.querySelectorAll('.incomplete-item').forEach(item => {
                      item.addEventListener('click', () => {
                        const itemIndex = parseInt(item.getAttribute('data-item-index'));
                        const selectedItem = incompleteItems[itemIndex];
                        Swal.close();
                        navigateToIncompleteContent(selectedItem);
                      });
                    });

                    const viewMoreBtn = document.getElementById('view-more-btn');
                    if (viewMoreBtn) {
                      viewMoreBtn.addEventListener('click', () => {
                        Swal.update({
                          html: createIncompleteItemsList(incompleteItems, true)
                        });
                      });
                    }
                  }, 50);
                });
              }
            }, 50);
          });
        }
      }
    });
  };

  // Function to store first sequence data in session storage
  const storeFirstSequenceInSession = () => {
    if (modules.length > 0) {
      for (let moduleIndex = 0; moduleIndex < modules.length; moduleIndex++) {
        const module = modules[moduleIndex];
        const sequences = Array.isArray(module.sequences)
          ? module.sequences
          : Object.values(module.sequences);

        if (sequences.length > 0) {
          const firstSequence = sequences[0];
          const unitNumber = moduleIndex + 1;

          let contentType = 'chapter';
          if (firstSequence.type === 'Quiz') {
            contentType = 'quiz';
          }

          const firstSequenceData = {
            activeContent: {
              type: contentType,
              unit: unitNumber,
              navIdx: 0,
              index: 0
            },
            sequenceId: firstSequence.sequence_id,
            chapterId: firstSequence.id,
            courseId: data.id
          };

          sessionStorage.setItem("firstSequenceData", JSON.stringify(firstSequenceData));
          break;
        }
      }
    }
  };

  useEffect(() => {
    if (data && modules.length > 0) {
      storeFirstSequenceInSession();
    }
  }, [data, modules]);

  if (!modules.length) {
    return (
      <div className="text-center py-5">
        <div className="mb-4">
          <i className="ri-folder-open-line text-muted" style={{ fontSize: '48px' }}></i>
        </div>
        <h5 className="text-muted mb-2">No Content Available</h5>
        <p className="text-muted">This course doesn't have any lessons yet.</p>
      </div>
    );
  }

  const handleSequenceClick = (sequence, moduleIndex, sequenceIndex) => {
    const lockStatus = isSequenceLocked(sequence, moduleIndex);

    if (lockStatus.locked) {
      if (lockStatus.reason === 'date_specific') {
        showDateSpecificLockedModal(sequence);
      } else if (lockStatus.reason === 'prerequisites') {
        showIncompleteContentModal(moduleIndex);
      }
      return;
    }

    const unitNumber = moduleIndex + 1;
    const module = modules[moduleIndex];
    const allSequences = Array.isArray(module.sequences)
      ? module.sequences.filter(
        (sq) =>
          sq.type === "Chapter" ||
          (sq.type === "Quiz" || sq.sub_type === "exam") ||
          sq.type === "Assignment"
      )
      : Object.values(module.sequences);

    let contentType = 'chapter';
    if (sequence.type === 'Quiz') {
      contentType = 'quiz';
    }

    const actualIndex = allSequences.findIndex(seq => seq.sequence_id === sequence.sequence_id);

    const payload = {
      activeContent: {
        type: contentType,
        unit: unitNumber,
        navIdx: actualIndex,
        index: actualIndex,
      },
      sequenceId: sequence.sequence_id,
      chapterId: sequence.id,
      courseId: data.id,
    };

    sessionStorage.setItem("courseViewState", JSON.stringify(payload));
    navigate("/course-view", { state: payload });
  };

  return (
    <div>
      <h5 className="mb-2">{title}</h5>
      <hr />
      <div className="accordion custom-accordionwithicon accordion-fill-success" id="accordionFill">
        {modules.map((module, mIdx) => {
          const sequences = Array.isArray(module.sequences)
            ? module.sequences
            : Object.values(module.sequences);

          // Separate materials from regular sequences
          // Materials have element_type: "Material"
          const materials = sequences.filter(seq =>
            seq.element_type === "Material"
          );

          // Non-material sequences have 'type' property (Chapter, Quiz, etc.)
          const nonMaterialSequences = sequences.filter(seq => seq.type);

          return (
            <div
              className="accordion-item mb-3 border rounded-3 shadow-sm p-1"
              key={module.id}
            >
              <h2 className="accordion-header" id={`accordionFillExample${mIdx}`}>
                <button
                  className={`accordion-button fw-semibold p-3 ${mIdx !== 0 ? 'collapsed' : ''}`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#accor_fill${mIdx}`}
                  aria-expanded={mIdx === 0 ? "true" : "false"}
                  aria-controls={`accor_fill${mIdx}`}
                >
                  <i className="ri-folder-line me-2"></i>
                  {module.module_title}
                </button>
              </h2>
              <div
                id={`accor_fill${mIdx}`}
                className={`accordion-collapse collapse ${mIdx === 0 ? 'show' : ''}`}
                aria-labelledby={`accordionFillExample${mIdx}`}
                data-bs-parent="#accordionFill"
              >
                <div className="accordion-body p-3">
                  <div className="list-group list-group-fill-success">
                    {nonMaterialSequences.length > 0 ? (
                      nonMaterialSequences.map((seq, seqIdx) => {
                        const lockStatus = isSequenceLocked(seq, mIdx);
                        const isLocked = lockStatus.locked;

                        let lockIcon = '';
                        if (isLocked) {
                          if (lockStatus.reason === 'date_specific') {
                            lockIcon = <i className="ri-time-line ms-2 text-warning"></i>;
                          } else {
                            lockIcon = <i className="ri-lock-line ms-2 text-warning"></i>;
                          }
                        }

                        return (
                          <div
                            key={seq.sequence_id}
                            className={`list-group-item list-group-item-action transition-all duration-300 p-3 ${isLocked
                              ? 'bg-light text-muted opacity-60'
                              : 'hover:bg-light cursor-pointer'
                              }`}
                            onClick={() => handleSequenceClick(seq, mIdx, seqIdx)}
                            style={{
                              cursor: 'pointer',
                              opacity: isLocked ? 0.6 : 1
                            }}
                          >
                            <div className="row align-items-center">
                              <div className="col-md-9 d-flex align-items-center">
                                <i className={`${seq.type === 'Quiz'
                                  ? (seq.sub_type === 'exam' ? 'ri-file-text-line' : 'ri-question-line')
                                  : 'ri-play-circle-line'
                                  } ${isLocked ? 'text-muted' : 'text-primary'} fs-5 me-2`}></i>
                                <span className={`fw-medium ${isLocked ? 'text-muted' : ''}`}>
                                  {seq.title}
                                  {lockIcon}
                                </span>
                              </div>
                              <div className="col-md-3 text-start">
                                {seq.duration && (
                                  <span className={`badge ${isLocked ? 'bg-secondary' : 'bg-light text-dark'} rounded-pill px-1 py-1`}>
                                    <i className="ri-time-line me-1"></i>
                                    {seq.duration}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-4 px-3">
                        <i className="ri-information-line text-muted fs-4 d-block mb-2"></i>
                        <p className="text-muted mb-0">No lessons available in this module yet.</p>
                      </div>
                    )}

                    {/* Materials Section - Only for Institute role */}
                    {role === "Institute" && materials.length > 0 && (
                      <div className="mt-3">
                        <div>
                          <h6 className="text-muted mb-2 d-flex align-items-center">
                            <i className="ri-file-download-line me-2"></i>
                            Materials
                          </h6>
                          {materials.map((mat, matIdx) => (
                            <div
                              key={`material-${mat.element_id || matIdx}`}
                              className="list-group-item list-group-item-action p-3 mb-2 hover:bg-light cursor-pointer"
                              onClick={() => handleMaterialDownload(mat)}
                              style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <i className="ri-file-text-line text-success fs-5 me-2"></i>
                                  <div>
                                    <span className="fw-medium text-dark">
                                      {mat.filename || 'Download Material'}
                                    </span>
                                  </div>
                                </div>
                                <i className="ri-download-2-line text-success fs-5"></i>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LessonContent;